import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';

export interface ReportCategoryOption {
  value: string;
  label: string;
}

interface ReportContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  categories: ReportCategoryOption[];
  onSubmit: (category: string, details: string) => Promise<unknown>;
}

export function ReportContentDialog({
  open, onOpenChange, title, categories, onSubmit,
}: ReportContentDialogProps) {
  const [category, setCategory] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setCategory('');
      setDetails('');
      setError('');
    }
  }, [open]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!category || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit(category, details);
      onOpenChange(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to submit report. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) onOpenChange(next); }}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto border"
        style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription style={{ color: 'var(--app-text-muted)' }}>
            Tell our safety team what concerns you. Only authorized reviewers can see your report.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="safety-report-category" className="text-sm">Reason</label>
            <select
              id="safety-report-category"
              required
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className="w-full min-h-11 rounded-lg border px-3 text-sm"
              style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
            >
              <option value="">Choose a reason</option>
              {categories.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="safety-report-details" className="text-sm">Details (optional)</label>
            <textarea
              id="safety-report-details"
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              maxLength={1000}
              rows={4}
              className="w-full rounded-lg border px-3 py-2 text-sm"
              style={{ background: 'var(--app-bg)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
              placeholder="Add context that will help us review this"
            />
            <span className="text-xs" style={{ color: 'var(--app-text-muted)' }}>
              {details.length}/1000 characters
            </span>
          </div>
          {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={!category || submitting}
            className="min-h-11 rounded-xl px-4 text-sm font-medium disabled:opacity-50"
            style={{ background: 'var(--app-accent-solid)', color: '#fff' }}
          >
            {submitting ? 'Submitting…' : 'Submit report'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
